import React, { useState, useEffect } from 'react';
import MarketingLayout from './Layouts/MarketingLayout';
import { Link } from '@inertiajs/react';

export default function Pricing({ pricingEstimator }) {
  const { currencies, cycles, defaultCurrency, defaultCycle, bands, solutions, pricings } = pricingEstimator;

  const [currency, setCurrency] = useState(defaultCurrency || 'NGN');
  const [cycle, setCycle] = useState(defaultCycle || 'annually');
  const [selectedBandId, setSelectedBandId] = useState(null);
  const [selectedSolutions, setSelectedSolutions] = useState([]);

  // Initialize selected band with the first band in the list
  useEffect(() => {
    if (bands && bands.length > 0) {
      setSelectedBandId(bands[0].id);
    }
  }, [bands]);

  // Handle solution selection toggle
  const toggleSolution = (id) => {
    setSelectedSolutions((prev) =>
      prev.includes(id) ? prev.filter((solId) => solId !== id) : [...prev, id]
    );
  };

  // Find the selected band object
  const selectedBand = bands ? bands.find((b) => b.id === selectedBandId) : null;

  // Calculate Base Platform Fee
  const getBaseFee = () => {
    if (!selectedBand) return 0;
    
    // Check if the band has prices defined for current currency and cycle
    const priceObj = selectedBand.prices?.find(
      (p) => p.currency === currency && p.billing_cycle === cycle
    );

    if (priceObj) {
      return priceObj.rate;
    }

    // Fallback to legacy single value if matching current currency
    if (selectedBand.currency === currency && selectedBand.billing_cycle === cycle) {
      return selectedBand.base_fee;
    }

    return 0;
  };

  // Get Pricing for a specific solution
  const getSolutionPrice = (solId) => {
    // Look up in pricings array
    const priceObj = pricings?.find(
      (p) =>
        p.solution_id === solId &&
        p.currency === currency &&
        p.billing_cycle === cycle &&
        p.is_active
    );

    return priceObj ? priceObj.amount : 0;
  };

  // Calculate Total Cost
  const baseFee = getBaseFee();
  const solutionCosts = selectedSolutions.reduce((sum, solId) => {
    return sum + getSolutionPrice(solId);
  }, 0);
  const totalCost = baseFee + solutionCosts;

  // Helper to format currency values
  const formatMoney = (amount) => {
    const symbol = currency === 'NGN' ? '₦' : currency === 'GHS' ? 'GH₵' : currency + ' ';
    return symbol + amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <MarketingLayout title="Pricing Estimator">
      {/* Hero Header */}
      <section className="spx-hero" style={{ paddingBottom: '32px' }}>
        <div className="spx-container animate-fade-in">
          <span className="spx-badge spx-badge-blue" style={{ marginBottom: '16px' }}>Interactive quote builder</span>
          <h1 className="spx-title-large">Simple, capacity-band based pricing.</h1>
          <p className="spx-body-lead" style={{ maxWidth: '640px', margin: '0 auto' }}>
            No per-student multipliers. Build your custom school plan by choosing your student capacity band and adding only the modules you need.
          </p>
        </div>
      </section>

      {/* Calculator Grid */}
      <section className="spx-section" style={{ backgroundColor: 'var(--bg-secondary)', borderTop: '1px solid var(--border-primary)' }}>
        <div className="spx-container">
          <div className="spx-pricing-grid">
            
            {/* Control Column */}
            <div className="spx-pricing-control-card">
              {/* Currency & Cycle Switchers */}
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '32px', borderBottom: '1px solid var(--border-primary)', paddingBottom: '24px' }}>
                {currencies && currencies.length > 1 && (
                  <div className="spx-form-group" style={{ margin: 0, flex: '1 1 200px' }}>
                    <label className="spx-label">Budget Currency</label>
                    <select 
                      className="spx-select" 
                      value={currency} 
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      {currencies.map((curr) => (
                        <option key={curr} value={curr}>{curr}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="spx-form-group" style={{ margin: 0, flex: '1 1 200px' }}>
                  <label className="spx-label">Billing Frequency</label>
                  <select 
                    className="spx-select" 
                    value={cycle} 
                    onChange={(e) => setCycle(e.target.value)}
                  >
                    {cycles && cycles.map((cyc) => (
                      <option key={cyc} value={cyc}>
                        {cyc.charAt(0).toUpperCase() + cyc.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Band Selector */}
              <div style={{ marginBottom: '40px' }}>
                <h3 className="spx-title-small" style={{ fontSize: '20px', marginBottom: '8px' }}>1. Select Student Capacity Band</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                  Choose the band corresponding to your active student population.
                </p>
                
                <div className="spx-band-selector">
                  {bands && bands.map((band) => (
                    <button
                      key={band.id}
                      type="button"
                      className={`spx-band-btn ${selectedBandId === band.id ? 'active' : ''}`}
                      onClick={() => setSelectedBandId(band.id)}
                    >
                      {band.label} Students
                    </button>
                  ))}
                </div>
              </div>

              {/* Solutions / Add-ons Selector */}
              <div>
                <h3 className="spx-title-small" style={{ fontSize: '20px', marginBottom: '8px' }}>2. Choose Active Solutions</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                  Select the modular suites you want live this term. You can change these options at any time.
                </p>

                {solutions && solutions.map((sol) => {
                  const isActive = selectedSolutions.includes(sol.id);
                  const price = getSolutionPrice(sol.id);
                  return (
                    <div 
                      key={sol.id} 
                      className={`spx-select-solution-card ${isActive ? 'active' : ''}`}
                      onClick={() => toggleSolution(sol.id)}
                    >
                      <div className="spx-checkbox">
                        {isActive && <i className="fas fa-check" style={{ fontSize: '11px' }}></i>}
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-primary)' }}>{sol.label}</span>
                          <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-primary)' }}>
                            {price > 0 ? formatMoney(price) : 'Included / Pending'}
                          </span>
                        </div>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.4' }}>
                          {sol.overview?.replace(/<[^>]*>/g, '') || 'Platform extension module.'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary Column */}
            <div className="spx-pricing-summary-card">
              <div>
                <h3 className="spx-title-small" style={{ fontSize: '22px', borderBottom: '1px solid var(--border-primary)', paddingBottom: '16px', marginBottom: '24px' }}>
                  Quote Summary
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Capacity Band ({selectedBand?.label || '0'} students)</span>
                    <span style={{ fontWeight: '600' }}>{formatMoney(baseFee)}</span>
                  </div>

                  {selectedSolutions.map((solId) => {
                    const sol = solutions.find((s) => s.id === solId);
                    const cost = getSolutionPrice(solId);
                    return (
                      <div key={solId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>+ {sol?.label}</span>
                        <span style={{ fontWeight: '600' }}>{formatMoney(cost)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: '2px solid var(--border-secondary)', paddingTop: '24px', marginBottom: '32px' }}>
                  <span style={{ fontWeight: '700', fontSize: '18px' }}>Total Estimate</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '800', fontSize: '32px', color: 'var(--text-primary)', lineHeight: '1' }}>
                      {formatMoney(totalCost)}
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '700' }}>
                      billed {cycle}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <a href={`/onboarding?band=${selectedBandId}&solutions=${selectedSolutions.join(',')}`} className="spx-btn spx-btn-primary" style={{ width: '100%' }}>
                    Start Free Pilot <i className="fas fa-arrow-right"></i>
                  </a>
                  <Link href="/contact" className="spx-btn spx-btn-secondary" style={{ width: '100%' }}>
                    Speak with Sales
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Service Agreement & FAQs */}
      <section className="spx-section" style={{ borderTop: '1px solid var(--border-primary)' }}>
        <div className="spx-container" style={{ maxWidth: '800px' }}>
          <h3 className="spx-title-medium" style={{ fontSize: '28px', textAlign: 'center', marginBottom: '40px' }}>Transparent pricing guidelines</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div className="spx-glass-card" style={{ padding: '24px' }}>
              <h4 className="spx-title-small" style={{ fontSize: '16px', marginBottom: '8px' }}><i className="fas fa-arrows-spin" style={{ color: 'var(--accent-blue)', marginRight: '8px' }}></i> Phased Rollout Support</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
                You do not have to purchase all modules at once. Our team recommends choosing one module (e.g. Results) for your first term, proving its value, and activating subsequent modules when comfortable.
              </p>
            </div>
            <div className="spx-glass-card" style={{ padding: '24px' }}>
              <h4 className="spx-title-small" style={{ fontSize: '16px', marginBottom: '8px' }}><i className="fas fa-wallet" style={{ color: 'var(--accent-blue)', marginRight: '8px' }}></i> Transaction collection fee</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
                Fee Collections module has an optional payment routing fee (1% or ₦100 per transaction, whichever applies) to cover gateway settlements. You can choose to pass this fee to the payer or absorb it as a business.
              </p>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
